import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchBar, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable";
import LoadingState from "@/components/LoadingState";
import EmptyState from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls/PaginationControls";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { UnauthorizedView } from "@/components/UnauthorizedView/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import {
  customerSupportUsersService,
  SEARCH_PAGE_SIZE,
  type CsWebsiteUserSummary,
} from "@/api/customerSupportUsers.service";
import { ROUTES } from "@/routes";
import sharedStyles from "../UserManagement.module.css";
import styles from "./CustomerSupport.module.css";
import { CsStatusChip } from "./csStatus";

// Reading the panel needs staff.website_users.read; staff.website_users.write is the separate
// grant for the two write actions and is checked in UserProfile, not here. Gating the whole
// page on .write locked out every agent who holds only the read grant.
const WEBSITE_USERS_READ_PERMISSION = "staff.website_users.read";

const COLUMNS: Column<CsWebsiteUserSummary>[] = [
  { header: "Name", accessor: "fullName" },
  { header: "Email", accessor: "email" },
  {
    header: "Company",
    accessor: "company",
    render: value => (value ? String(value) : "Not provided"),
  },
  {
    header: "Verification status",
    accessor: "verificationStatus",
    render: value => <CsStatusChip status={String(value)} />,
  },
  {
    header: "Access tier",
    accessor: "accessTier",
    render: value => (value ? String(value) : "—"),
  },
];

function CustomerSupportResults() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // 1-based, mirroring PaginationControls.
  const [page, setPage] = useState(1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce keystrokes (~300ms) into the `search` dep that drives the query.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  function commitSearch() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearch(searchInput.trim());
  }

  // Any change of term re-filters the whole result set, so page 2 of the old term is
  // meaningless against the new one — always land back on page 1.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const offset = (page - 1) * SEARCH_PAGE_SIZE;

  // The list is a paginated browse, not a search-only view: the first page loads on mount
  // with NO term, and the search box narrows it.
  const { data, loading, error, refetch } = useQuery(
    () =>
      customerSupportUsersService.searchUsers({
        search: search || undefined,
        limit: SEARCH_PAGE_SIZE,
        offset,
      }),
    [search, offset],
  );

  const status = (error as Error & { status?: number })?.status;
  const isAuthError = status === 401 || status === 403;

  function renderResults() {
    if (isAuthError) {
      return <UnauthorizedView />;
    }

    if (loading) {
      return <LoadingState />;
    }

    if (error && !isAuthError) {
      return (
        <EmptyState
          variant="error"
          title="Couldn't load results"
          message={error.message}
          action={{ label: "Retry", onClick: refetch }}
        />
      );
    }

    const table = (
      <div className={styles.tableContainer}>
        <DataTable
          data={data?.data ?? []}
          columns={COLUMNS}
          rowKey="userId"
          size="small"
          emptyMessage={search ? "No matching users found." : "No website users found."}
          onRowClick={row => navigate(ROUTES.users.websiteDetail(row.userId))}
        />
      </div>
    );

    // The count line and the pager both describe a window into a larger result set, so
    // with no rows there is no window to describe — the table's own empty message is all
    // that is left to say.
    if (!data || data.data.length === 0) {
      return table;
    }

    const { total, limit } = data.meta;

    // Truncation guarantee: a page is never presented as the complete answer. The count
    // line always states the current window against `meta.total`, and whenever
    // `total > limit` the pagination control is offered so the rest is reachable.
    return (
      <>
        <p className={styles.resultCount}>
          {total > limit
            ? `Showing ${offset + 1}–${Math.min(offset + data.data.length, total)} of ${total} ${total === 1 ? "match" : "matches"}`
            : `${total} ${total === 1 ? "match" : "matches"}`}
        </p>

        {table}

        <PaginationControls
          currentPage={page}
          totalPages={Math.ceil(total / limit)}
          startIndex={offset}
          endIndex={offset + data.data.length}
          totalCount={total}
          itemName="website users"
          onPageChange={setPage}
        />
      </>
    );
  }

  return (
    <div className={sharedStyles.wrapper}>
      {/* .wrapper pulls its first child out to the viewport edge, so PageHeader needs this
          plain div to absorb that — its own padding then reads as a full-bleed band. */}
      <div>
        <PageHeader title="Website Users" subtitle="Manage platform user accounts" />
      </div>

      <div className={sharedStyles.searchSection}>
        <div className={styles.searchToolbar}>
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={e => {
              e.preventDefault();
              commitSearch();
            }}
            onClear={() => {
              setSearchInput("");
              setSearch("");
            }}
            placeholder="Search by email, name, or company"
          />
        </div>
      </div>

      <div className={sharedStyles.contentSection}>{renderResults()}</div>
    </div>
  );
}

export default function CustomerSupportPage() {
  return (
    <PermissionGate permission={WEBSITE_USERS_READ_PERMISSION} fallback={<UnauthorizedView />}>
      <CustomerSupportResults />
    </PermissionGate>
  );
}
